import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookmarkPlus, Info, CheckCircle, X } from 'lucide-react';
import useTokenStore from '../../store/store';
import { ThemeProps } from '../../utils/theme';
import { readableTextColour, visibleAccent } from '../../utils/colourContrast';
import { createSavedSearch } from '../../utils/savedSearches';
import {
  buildCriteriaFromListingFilters,
  suggestListingSearchName,
  ListingFilterState,
} from '../../utils/listingSearchCapture';
import { formatSearchCriteria } from '../../utils/formatSearchCriteria';
import { SAVED_SEARCH_NAME_MAX_LENGTH } from '../../constants/savedSearchLimits';

const EXPLAINER_COPY =
  "Save these filters and we'll notify you here and by email when a new property matches. You can edit or delete it any time under Saved Searches in your account menu.";

interface SaveSearchButtonProps {
  filters: ListingFilterState;
  getThemes: ThemeProps;
  /** Called instead of opening the modal when no buyer is signed in — the
      listing page owns its login round-trip (filter stash + redirect). */
  onRequireLogin: () => void;
}

const SaveSearchButton: React.FC<SaveSearchButtonProps> = ({
  filters,
  getThemes,
  onRequireLogin,
}) => {
  const getToken = useTokenStore((state) => state.token);

  const [infoOpen, setInfoOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const infoRef = useRef<HTMLDivElement>(null);

  const criteria = buildCriteriaFromListingFilters(filters);
  const hasCriteria = Object.keys(criteria).length > 0;

  // Close the info popover on any outside click.
  useEffect(() => {
    if (!infoOpen) return;
    const onDocClick = (event: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(event.target as Node)) {
        setInfoOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [infoOpen]);

  // Prefer colour 2, but skip it when white — a white button on the white
  // listing panel and modal card is invisible.
  const themeColour = visibleAccent(getThemes?.color2 || '', getThemes?.color1, '#1A2347');
  const themeText = readableTextColour(themeColour);

  const openModal = () => {
    if (!getToken) {
      onRequireLogin();
      return;
    }
    setName(suggestListingSearchName(criteria));
    setEmailEnabled(true);
    setError(null);
    setSaved(false);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!getToken || saving) return;
    if (!name.trim()) {
      setError('Please name your search.');
      return;
    }
    setSaving(true);
    setError(null);
    const result = await createSavedSearch(getToken, {
      name: name.trim(),
      criteria,
      emailEnabled,
    });
    setSaving(false);
    if (result.ok) {
      setSaved(true);
    } else {
      setError(result.message || 'Something went wrong — please try again.');
    }
  };

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={openModal}
        disabled={!hasCriteria}
        title={
          hasCriteria
            ? 'Save this search and get alerts for new matches'
            : 'Choose at least one filter (region, type, strategy or price) first'
        }
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
          hasCriteria ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
        }`}
        style={{
          backgroundColor: hasCriteria ? themeColour : 'white',
          color: hasCriteria ? themeText : '#374151',
          border: hasCriteria ? 'none' : '1px solid #D1D5DB',
        }}
      >
        <BookmarkPlus className="w-4 h-4" />
        Save search
      </button>

      <div className="relative" ref={infoRef}>
        <button
          onClick={() => setInfoOpen((open) => !open)}
          aria-label="About saved searches"
          className="p-1.5 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <Info className="w-4 h-4" />
        </button>
        {infoOpen && (
          <div className="absolute left-0 top-full mt-2 w-72 z-30 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-600">
            {EXPLAINER_COPY}
          </div>
        )}
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[1001]"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl"
            style={{ backgroundColor: "white" }}
            onClick={(e) => e.stopPropagation()}
          >
            {saved ? (
              <div className="text-center">
                <CheckCircle className="w-10 h-10 mx-auto mb-3" style={{ color: themeColour }} />
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Search saved</h3>
                <p className="text-sm text-gray-600 mb-5">
                  We&rsquo;ll let you know when a new property matches. You can edit it any
                  time in Saved Searches.
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setModalOpen(false)}
                    className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                  >
                    Done
                  </button>
                  <Link
                    to="/saved-searches"
                    className="px-4 py-2 text-sm font-medium rounded-md"
                    style={{ backgroundColor: themeColour, color: themeText }}
                  >
                    View saved searches
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="text-lg font-semibold text-gray-900">Save this search</h3>
                  <button
                    onClick={() => setModalOpen(false)}
                    aria-label="Close"
                    className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">{formatSearchCriteria(criteria)}</p>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search name
                </label>
                <input
                  type="text"
                  maxLength={SAVED_SEARCH_NAME_MAX_LENGTH}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm mb-3"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <label className="flex items-center text-sm text-gray-700 gap-2 mb-3">
                  <input
                    type="checkbox"
                    checked={emailEnabled}
                    onChange={(e) => setEmailEnabled(e.target.checked)}
                  />
                  Email me when new properties match
                </label>
                <p className="text-xs text-gray-500 mb-4">{EXPLAINER_COPY}</p>
                {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setModalOpen(false)}
                    className="px-4 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50"
                    style={{ backgroundColor: themeColour, color: themeText }}
                  >
                    {saving ? 'Saving…' : 'Save search'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SaveSearchButton;
